import { useState } from 'react'
import { useAccess } from '@/access/useAccess'
import type { ModerationItem } from '@/api/pending'
import { SUBTITLES } from '@/shell/sectionCopy'
import { Card, EmptyRow, PageHeader, Segmented, Spinner } from '@/ui'
import styles from './ApprovalsView.module.css'
import { FeedPreviewModal } from './FeedPreviewModal'
import { ReviewCard } from './ReviewCard'
import { useModerationQueues } from './useModerationQueues'
import { useReviewAction } from './useReviewAction'

type Tab = 'queue' | 'reported'

const EMPTY: Record<Tab, { title: string; description: string }> = {
  queue: {
    title: 'Nada para revisar',
    description: 'Todas as publicações e enquetes dos moradores foram tratadas.',
  },
  reported: {
    title: 'Nenhuma denúncia aberta',
    description: 'Os vizinhos não sinalizaram nada desde sua última visita.',
  },
}

export function ApprovalsView() {
  const { condoId, membership } = useAccess()
  const { queue, reported, isLoading } = useModerationQueues(condoId)
  const review = useReviewAction(condoId)
  const [tab, setTab] = useState<Tab>('queue')
  const [preview, setPreview] = useState<ModerationItem | null>(null)

  const items = tab === 'queue' ? queue : reported

  return (
    <>
      <PageHeader
        title="Aprovações"
        subtitle={SUBTITLES.approvals({ condoName: membership.condominium_name, waiting: 0 })}
      />
      <div className={styles.toolbar}>
        <Segmented
          aria-label="Filas de moderação"
          value={tab}
          onChange={setTab}
          items={[
            { id: 'queue', label: 'Aguardando revisão', count: queue.length },
            { id: 'reported', label: 'Denunciados', count: reported.length },
          ]}
        />
        <span className={styles.order}>Mais antigos primeiro</span>
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner size={28} />
        </div>
      ) : items.length === 0 ? (
        <Card pad={0}>
          <EmptyRow icon="check" {...EMPTY[tab]} />
        </Card>
      ) : (
        <div
          className={styles.list}
          role="tabpanel"
          aria-label={tab === 'queue' ? 'Aguardando revisão' : 'Denunciados'}
        >
          {items.map((item) => (
            <ReviewCard
              key={`${item.kind}-${item.id}`}
              item={item}
              mode={tab}
              onAction={(action, it) => review.mutate({ action, item: it })}
              onPreview={setPreview}
            />
          ))}
        </div>
      )}

      {preview && <FeedPreviewModal item={preview} onClose={() => setPreview(null)} />}
    </>
  )
}
